import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Grid,
  GridColumn,
  GridCellProps,
} from "@progress/kendo-react-grid";
import { TextArea } from "@progress/kendo-react-inputs";
import CleanDatePicker from "src/dm/components/CleanDatePicker/CleanDatePicker";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { apiClient } from "src/dm/utils/axios";

interface TermsRow {
  termsId: number;
  body: string | null;
  isActive: boolean;
  activeDate: string | null;
  createdOn: string | null;
}

interface ListResponse {
  items: TermsRow[];
  total: number;
}

interface TermsWriteRequest {
  termsId?: number;
  body: string;
  isActive: boolean;
  activeDate: string | null;
}

const baseUrl = "/dm/V1/admin/terms";
const emptyDraft = (): TermsWriteRequest => ({
  body: "",
  isActive: false,
  activeDate: null,
});

type Toast = { kind: "ok" | "err"; message: string } | null;

const extractApiMessage = (err: unknown, fallback: string): string => {
  const e = err as {
    response?: { status?: number; data?: { message?: string } };
    message?: string;
  };
  return e.response?.data?.message ?? e.message ?? fallback;
};

const TermsPage = () => {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState<TermsWriteRequest>(emptyDraft);
  const [editRow, setEditRow] = useState<
    (TermsWriteRequest & { termsId: number }) | null
  >(null);
  const [toast, setToast] = useState<Toast>(null);

  // Auto-dismiss toast after a few seconds — short enough to feel like a
  // toast, long enough that the user can read the 409 message.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["terms"],
    queryFn: async (): Promise<TermsRow[]> => {
      const response = await apiClient.get<ListResponse>(baseUrl);
      return response.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (body: TermsWriteRequest) => {
      const response = await apiClient.post<TermsRow>(baseUrl, body);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["terms"] });
      setToast({ kind: "ok", message: "New revision saved (inactive)." });
    },
    onError: (err) =>
      setToast({ kind: "err", message: extractApiMessage(err, "Save failed") }),
  });

  const update = useMutation({
    mutationFn: async (body: TermsWriteRequest & { termsId: number }) => {
      const response = await apiClient.put<TermsRow>(
        `${baseUrl}/${body.termsId}`,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["terms"] });
      setToast({ kind: "ok", message: "Revision updated." });
    },
    onError: (err) =>
      setToast({ kind: "err", message: extractApiMessage(err, "Save failed") }),
  });

  const activate = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.post(`${baseUrl}/${id}/activate`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["terms"] });
      setToast({ kind: "ok", message: "Activated. Prior active row was retired." });
    },
    onError: (err) =>
      setToast({
        kind: "err",
        message: extractApiMessage(err, "Activate failed"),
      }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`${baseUrl}/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["terms"] });
      setToast({ kind: "ok", message: "Revision deleted." });
    },
    onError: (err) =>
      setToast({
        kind: "err",
        message: extractApiMessage(err, "Delete failed"),
      }),
  });

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as TermsRow;
    return (
      <td>
        {!row.isActive && (
          <Button
            type="button"
            size="small"
            fillMode="flat"
            themeColor="primary"
            disabled={activate.isPending}
            onClick={() => activate.mutate(row.termsId)}
          >
            Activate
          </Button>
        )}
        <Button
          type="button"
          size="small"
          fillMode="flat"
          disabled={row.isActive}
          title={
            row.isActive
              ? "Active rows are immutable — create a new revision instead."
              : ""
          }
          onClick={() =>
            setEditRow({
              termsId: row.termsId,
              body: row.body ?? "",
              isActive: row.isActive,
              activeDate: row.activeDate,
            })
          }
        >
          Edit
        </Button>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          themeColor="error"
          disabled={remove.isPending || row.isActive}
          title={
            row.isActive
              ? "Active rows cannot be deleted — activate a different revision first."
              : ""
          }
          onClick={() => {
            if (confirm(`Delete terms ${row.termsId}?`)) {
              remove.mutate(row.termsId);
            }
          }}
        >
          Delete
        </Button>
      </td>
    );
  };

  const ActiveCell = (props: GridCellProps) => {
    const row = props.dataItem as TermsRow;
    return (
      <td>
        {row.isActive ? (
          <span
            style={{
              background: "#dcfce7",
              color: "#166534",
              padding: "0.15rem 0.5rem",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            ACTIVE
          </span>
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        )}
      </td>
    );
  };

  const ActiveDateCell = (props: GridCellProps) => {
    const row = props.dataItem as TermsRow;
    return (
      <td>
        {row.activeDate ? new Date(row.activeDate).toLocaleDateString() : ""}
      </td>
    );
  };

  const CreatedOnCell = (props: GridCellProps) => {
    const row = props.dataItem as TermsRow;
    return (
      <td>
        {row.createdOn ? new Date(row.createdOn).toLocaleDateString() : ""}
      </td>
    );
  };

  const BodyPreviewCell = (props: GridCellProps) => {
    const row = props.dataItem as TermsRow;
    const text = row.body ?? "";
    return <td>{text.length > 60 ? text.slice(0, 60) + "…" : text}</td>;
  };

  const renderEditor = (
    row: TermsWriteRequest,
    setRow: (r: TermsWriteRequest) => void
  ) => (
    <div style={{ display: "grid", gap: "0.75rem", minWidth: "520px" }}>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Terms &amp; Conditions Body
        </span>
        <TextArea
          rows={14}
          value={row.body}
          onChange={(e) => setRow({ ...row, body: String(e.value ?? "") })}
        />
      </label>
      <div
        style={{
          display: "flex",
          gap: "1.25rem",
          alignItems: "center",
        }}
      >
        <label>
          <span style={{ fontSize: "0.8rem", color: "#475569" }}>
            Active date (label)
          </span>
          <CleanDatePicker
            value={row.activeDate ? new Date(row.activeDate) : null}
            onChange={(e) =>
              setRow({
                ...row,
                activeDate: e.value ? e.value.toISOString() : null,
              })
            }
          />
        </label>
      </div>
      <p
        style={{
          background: "#f1f5f9",
          padding: "0.5rem 0.75rem",
          borderRadius: "6px",
          fontSize: "0.8rem",
          color: "#475569",
          margin: 0,
        }}
      >
        New rows are saved as inactive. Use <strong>Activate</strong> on the
        row to make it the live version (any previously-active row is retired
        automatically).
      </p>
    </div>
  );

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Admin
      </Link>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Terms &amp; Conditions</h1>
          <p style={{ color: "#475569", margin: "0.25rem 0 0" }}>
            Manage Terms &amp; Conditions revisions. Active rows are immutable
            — to amend, add a new revision and activate it.
          </p>
        </div>
        <Button
          themeColor="primary"
          onClick={() => {
            setDraft(emptyDraft());
            setShowAdd(true);
          }}
        >
          Add revision
        </Button>
      </div>

      {toast && (
        <div
          role="alert"
          style={{
            background: toast.kind === "ok" ? "#dcfce7" : "#fef2f2",
            border:
              toast.kind === "ok"
                ? "1px solid #86efac"
                : "1px solid #fecaca",
            color: toast.kind === "ok" ? "#166534" : "#b91c1c",
            padding: "0.6rem 0.9rem",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            style={{
              background: "transparent",
              border: 0,
              fontSize: "1.1rem",
              cursor: "pointer",
              color: "inherit",
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {isLoading && <p>Loading…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}
        {data && data.length === 0 && (
          <p style={{ color: "#94a3b8" }}>No terms rows yet.</p>
        )}
        {data && data.length > 0 && (
          <Grid data={data}>
            <GridColumn field="termsId" title="ID" width="80px" />
            <GridColumn title="Terms" cells={{ data: BodyPreviewCell }} />
            <GridColumn title="Active" width="100px" cells={{ data: ActiveCell }} />
            <GridColumn
              title="Active Date"
              width="140px"
              cells={{ data: ActiveDateCell }}
            />
            <GridColumn
              title="Created"
              width="120px"
              cells={{ data: CreatedOnCell }}
            />
            <GridColumn title="" width="240px" cells={{ data: ActionsCell }} />
          </Grid>
        )}
      </section>

      {showAdd && (
        <Dialog title="Add revision" onClose={() => setShowAdd(false)}>
          {renderEditor(draft, setDraft)}
          <DialogActionsBar>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={create.isPending || !draft.body.trim()}
              onClick={async () => {
                try {
                  await create.mutateAsync(draft);
                  setShowAdd(false);
                } catch {
                  // toast already populated via onError; keep dialog open.
                }
              }}
            >
              {create.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {editRow && (
        <Dialog
          title={`Edit terms ${editRow.termsId}`}
          onClose={() => setEditRow(null)}
        >
          {renderEditor(editRow, (r) =>
            setEditRow({ ...editRow, ...r, termsId: editRow.termsId })
          )}
          <DialogActionsBar>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={update.isPending || !editRow.body.trim()}
              onClick={async () => {
                try {
                  await update.mutateAsync(editRow);
                  setEditRow(null);
                } catch {
                  // toast surfaces the 409 message; leave dialog open.
                }
              }}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default TermsPage;
